import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiX, FiCamera, FiSave, FiUser, FiBriefcase, FiTag, FiFileText, FiLoader } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../supabase/supabase';
import { SALVAGE_TAGS } from '../../data/salvageTags';

export const ProfileModal = ({ isOpen, onClose }) => {
  const { profile, refreshProfile } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    company_details: '',
    tags: []
  });

  // Avatar State
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const defaultAvatar = 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80';

  // Tag Input State
  const [tagInput, setTagInput] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (profile && isOpen) {
      setFormData({
        full_name: profile.full_name || '',
        company_name: profile.company_name || '',
        company_details: profile.company_details || '',
        tags: profile.tags || []
      });
      setAvatarPreview(profile.avatar_url || '');
      setAvatarFile(null);
      setTagInput('');
      setShowDropdown(false);
    }
  }, [profile, isOpen]);

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("Image must be less than 5MB");
        return;
      }
      setAvatarFile(file);
      // Create a local preview immediately so the user sees their selection
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const addTag = (tag) => {
    const cleanTag = tag.trim();
    if (cleanTag && !formData.tags.includes(cleanTag)) {
      setFormData({ ...formData, tags: [...formData.tags, cleanTag] });
    }
    setTagInput('');
    setShowDropdown(false);
  };

  const removeTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(tagInput);
    }
  };

  const filteredTags = SALVAGE_TAGS.filter(t => 
    t.toLowerCase().includes(tagInput.toLowerCase()) && 
    !formData.tags.includes(t)
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let finalAvatarUrl = profile.avatar_url;

      // 1. Upload new avatar if a file was selected
      if (avatarFile) {
        const fileExt = avatarFile.name.split('.').pop();
        const fileName = `${profile.id}-${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(fileName, avatarFile, { 
            upsert: true,
            cacheControl: '3600'
          });

        if (uploadError) {
          console.error('Storage Upload Error:', uploadError);
          throw new Error('Failed to upload image. Please check your connection.');
        }

        // Get the public URL for the uploaded image
        const { data: publicUrlData } = supabase.storage
          .from('avatars')
          .getPublicUrl(fileName);

        finalAvatarUrl = publicUrlData.publicUrl;
      }

      // 2. Update Profile Record in DB with .select() to verify success
      const { data: updateData, error: updateError } = await supabase
        .from('profiles_1783455000000')
        .update({
          full_name: formData.full_name,
          company_name: formData.company_name,
          company_details: formData.company_details,
          avatar_url: finalAvatarUrl,
          tags: formData.tags
        })
        .eq('id', profile.id)
        .select();

      if (updateError) throw updateError;
      if (!updateData || updateData.length === 0) {
         throw new Error('Profile update failed to save. Please try reloading the page.');
      }
      
      // Refresh Auth Context to instantly update Header
      await refreshProfile();
      onClose();
    } catch (error) {
      console.error('Error saving profile:', error);
      alert(`Failed to save profile updates: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }} 
          animate={{ opacity: 1, scale: 1, y: 0 }} 
          exit={{ opacity: 0, scale: 0.95, y: 20 }} 
          className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl my-8 overflow-hidden flex flex-col max-h-[90vh]"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 sticky top-0 z-10">
            <h2 className="text-xl font-bold text-gray-900">Edit Profile</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500">
              <SafeIcon icon={FiX} size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto flex-1 space-y-8">
            
            {/* Avatar Section */}
            <div className="flex flex-col items-center justify-center">
              <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-lg bg-gray-100 relative">
                  {avatarPreview ? (
                    <img 
                      src={avatarPreview} 
                      alt="Profile Preview" 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = defaultAvatar;
                      }}
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <SafeIcon icon={FiUser} size={40} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <SafeIcon icon={FiCamera} className="text-white text-xl" />
                  </div>
                </div>
              </div>
              <p className="text-xs text-brand font-bold mt-3 cursor-pointer hover:underline" onClick={() => fileInputRef.current?.click()}>
                Change Photo
              </p>
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleImageChange} 
                accept="image/*" 
                className="hidden" 
              />
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                  <SafeIcon icon={FiUser} /> Username / Full Name
                </label>
                <input 
                  type="text" 
                  value={formData.full_name} 
                  onChange={e => setFormData({...formData, full_name: e.target.value})}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 text-sm font-medium"
                  placeholder="John Doe"
                />
              </div>
              
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                  <SafeIcon icon={FiBriefcase} /> Company Name
                </label>
                <input 
                  type="text" 
                  value={formData.company_name} 
                  onChange={e => setFormData({...formData, company_name: e.target.value})}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 text-sm font-medium"
                  placeholder="TechRecycle ZA"
                />
              </div>

              <div className="space-y-1.5 md:col-span-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                  <SafeIcon icon={FiFileText} /> Company Details & Address
                </label>
                <textarea 
                  value={formData.company_details} 
                  onChange={e => setFormData({...formData, company_details: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 text-sm font-medium resize-none min-h-[80px]"
                  placeholder="Provide a brief description of your operations and physical address..."
                />
              </div>

              {/* Tags Section */}
              <div className="space-y-3 md:col-span-2 relative">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2 mb-1">
                  <SafeIcon icon={FiTag} /> Collection Categories & Niches
                </label>
                
                {/* Selected Tags Display */}
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-brand/10 text-brand border border-brand/20">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="hover:bg-brand/20 rounded-full p-0.5 transition-colors">
                        <SafeIcon icon={FiX} size={12} />
                      </button>
                    </span>
                  ))}
                  {formData.tags.length === 0 && (
                    <span className="text-xs text-gray-400 italic">No categories added yet.</span>
                  )}
                </div>

                {/* Tag Input & Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <input 
                    type="text" 
                    value={tagInput}
                    onChange={e => {
                      setTagInput(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    onKeyDown={handleTagKeyDown}
                    className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 text-sm shadow-sm"
                    placeholder="Search specific brands (e.g. Apple), categories, or niches..."
                  />
                  
                  {/* Dropdown Menu */}
                  <AnimatePresence>
                    {showDropdown && (
                      <motion.div 
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        className="absolute z-20 w-full mt-1 bg-white border border-gray-100 rounded-xl shadow-xl max-h-60 overflow-y-auto"
                      >
                        {tagInput.trim() && !filteredTags.some(t => t.toLowerCase() === tagInput.toLowerCase()) && (
                          <button 
                            onClick={() => addTag(tagInput)}
                            className="w-full text-left px-4 py-3 text-sm text-brand font-medium hover:bg-brand/5 border-b border-gray-50 flex items-center gap-2"
                          >
                            <SafeIcon icon={FiTag} /> Add custom: "{tagInput}"
                          </button>
                        )}
                        {filteredTags.length > 0 ? (
                          filteredTags.map((tag, index) => (
                            <button
                              key={index}
                              onClick={() => addTag(tag)}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              {tag}
                            </button>
                          ))
                        ) : (
                          !tagInput.trim() && <div className="px-4 py-3 text-sm text-gray-400 italic">Start typing to search hundreds of niches...</div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Add broad categories (e.g. Mobile Phones) or specific niches (e.g. Apple iPhone, Samsung Galaxy).</p>
              </div>

            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 sticky bottom-0">
            <button 
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-bold text-gray-600 hover:bg-gray-200 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2.5 bg-brand text-white rounded-xl text-sm font-bold hover:bg-brand-light transition-all shadow-md disabled:opacity-50"
            >
              {isSaving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSave} />}
              {isSaving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};